/**
 * Official NHC forecast tracks keyed to the selected STOFS forecast cycle.
 *
 * Browser JS on https://chl.crc.nd.edu cannot read NHC GIS / RAMMB / RAL
 * directly (CORS). Prefer the same-origin API:
 *
 *   GET /ofcl/storms?dtg=YYYYMMDDHH
 *
 * mounted via viewer/ofcl_flask.py on the existing Dash/Flask server.
 *
 * Fallback: NOAA MapServer forecast points (CORS works for chl.crc.nd.edu)
 * — latest advisory only, so older cycles will not change tracks until
 * /ofcl/storms is available.
 */

const OFCL_API = '/ofcl/storms';
const FORECAST_POINTS_URL =
  'https://mapservices.weather.noaa.gov/tropical/rest/services/tropical/' +
  'NHC_tropical_weather_summary/MapServer/5/query' +
  '?where=1%3D1&outFields=*&returnGeometry=true&f=geojson';

const MAX_INIT_LAG_HOURS = 18;
const MAX_INIT_LEAD_HOURS = 6;

const MONTHS = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
};

export function cycleKeyToDtg(cycle) {
  const s = String(cycle || '').trim();
  const m = s.match(/^(\d{4})-?(\d{2})-?(\d{2})[_]?(\d{2})$/);
  if (m) return `${m[1]}${m[2]}${m[3]}${m[4]}`;
  if (/^\d{10}$/.test(s)) return s;
  return null;
}

export function dtgToUtcMs(dtg) {
  const s = String(dtg);
  if (s.length !== 10) return NaN;
  return Date.UTC(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8), +s.slice(8, 10), 0, 0);
}

export function manifestTimeToUtcMs(ts) {
  const m = String(ts)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return NaN;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function lerpLon(a, b, t) {
  let d = b - a;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  let out = a + d * t;
  if (out > 180) out -= 360;
  if (out < -180) out += 360;
  return out;
}

export function pickClosestInitDtg(initDtgs, targetDtg) {
  const targetMs = dtgToUtcMs(targetDtg);
  if (!Number.isFinite(targetMs) || !initDtgs.length) return null;
  let best = null;
  let bestScore = Infinity;
  for (const dtg of initDtgs) {
    const ms = dtgToUtcMs(dtg);
    if (!Number.isFinite(ms)) continue;
    const hours = (ms - targetMs) / 3600000;
    if (hours < -MAX_INIT_LAG_HOURS || hours > MAX_INIT_LEAD_HOURS) continue;
    const score = hours <= 0 ? -hours : hours * 2;
    if (score < bestScore) {
      bestScore = score;
      best = dtg;
    }
  }
  return best;
}

export function interpolateOfclPosition(points, initDtg, validMs) {
  if (!points?.length || !Number.isFinite(validMs)) return null;
  const initMs = dtgToUtcMs(initDtg);
  if (!Number.isFinite(initMs)) return null;
  const hours = (validMs - initMs) / 3600000;
  if (hours < points[0].tau || hours > points[points.length - 1].tau) return null;

  let i = 0;
  while (i < points.length - 1 && points[i + 1].tau < hours) i++;
  const a = points[i];
  const b = points[Math.min(i + 1, points.length - 1)];
  if (a.tau === b.tau) return {...a, hours, lon: a.lon, lat: a.lat};
  const t = (hours - a.tau) / (b.tau - a.tau);
  return {
    hours,
    lon: lerpLon(a.lon, b.lon, t),
    lat: lerp(a.lat, b.lat, t),
    vmax: a.vmax != null && b.vmax != null ? lerp(a.vmax, b.vmax, t) : a.vmax ?? b.vmax,
    mslp: a.mslp != null && b.mslp != null ? lerp(a.mslp, b.mslp, t) : a.mslp ?? b.mslp,
    ty: t < 0.5 ? a.ty : b.ty
  };
}

function categoryColor(vmax) {
  const v = vmax == null ? 0 : vmax;
  if (v >= 137) return [120, 0, 120, 230];
  if (v >= 113) return [255, 0, 255, 230];
  if (v >= 96) return [255, 0, 0, 230];
  if (v >= 83) return [255, 100, 0, 230];
  if (v >= 64) return [255, 200, 0, 230];
  if (v >= 34) return [0, 200, 80, 230];
  return [80, 160, 255, 230];
}

function stormLabel(storm) {
  const id = `${storm.basin}${storm.cy}`;
  const name = storm.name && storm.name.toUpperCase() !== 'INVEST' ? storm.name : null;
  return name ? `${name} (${id})` : id;
}

function cleanStormName(raw) {
  if (!raw) return null;
  return String(raw)
    .replace(/^(Hurricane|Tropical Storm|Tropical Depression|Post-Tropical|Subtropical|Typhoon)\s+/i, '')
    .trim() || null;
}

function initDtgFromAdvisory(advdate, validtime) {
  const vm = String(validtime || '').match(/^(\d{1,2})\/(\d{2})(\d{2})$/);
  const am = String(advdate || '').match(/\b([A-Z][a-z]{2})\s+(\d{1,2})\s+(\d{4})\b/);
  if (!vm || !am) return null;
  const month = MONTHS[am[1]];
  if (!month) return null;
  return `${am[3]}${String(month).padStart(2, '0')}${String(+vm[1]).padStart(2, '0')}${vm[2]}`;
}

function normalizeStorm(s) {
  const points = (s.points || []).map(p => ({
    tau: p.tau,
    lat: p.lat,
    lon: p.lon,
    vmax: p.vmax ?? null,
    mslp: p.mslp ?? null,
    ty: p.ty ?? null
  }));
  return {
    id: s.id,
    basin: s.basin,
    cy: s.cy,
    name: s.name || null,
    initDtg: s.initDtg,
    points,
    path: s.path || points.map(p => [p.lon, p.lat, 0])
  };
}

async function readJsonResponse(res, label) {
  const text = await res.text();
  const trimmed = text.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) {
    throw new Error(`${label}: non-JSON (is /ofcl/storms mounted on the server?)`);
  }
  return JSON.parse(trimmed);
}

/** Same-origin RAMMB-backed API (cycle-accurate). */
async function loadFromOfclApi(targetDtg) {
  const url = `${OFCL_API}?dtg=${encodeURIComponent(targetDtg)}`;
  const res = await fetch(url, {cache: 'no-cache'});
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  const payload = await readJsonResponse(res, url);
  const storms = Array.isArray(payload?.storms) ? payload.storms.map(normalizeStorm) : [];
  if (payload?.stats) console.info('[ofcl] /ofcl/storms', payload.stats);
  return storms;
}

/** MapServer latest advisories (CORS OK on chl.crc.nd.edu; not cycle-historical). */
export function stormsFromForecastGeoJson(fc) {
  const groups = new Map();
  for (const feature of fc?.features || []) {
    const p = feature.properties || {};
    const basin = String(p.basin || '').toUpperCase();
    const cy = String(Math.round(Number(p.stormnum))).padStart(2, '0');
    if (!basin || cy === 'NaN') continue;
    const tau = Number(p.tau);
    if (!Number.isFinite(tau) || tau < 0) continue;
    const coords = feature?.geometry?.coordinates;
    let lon = Number(coords?.[0]);
    let lat = Number(coords?.[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
      lon = Number(p.lon);
      lat = Number(p.lat);
    }
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;

    const key = p.idp_subset || `${basin.toLowerCase()}${cy}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        basin,
        cy,
        name: cleanStormName(p.stormname),
        advisnum: p.advisnum || null,
        advdate: p.advdate || null,
        pointsByTau: new Map()
      };
      groups.set(key, g);
    }
    if (!g.pointsByTau.has(tau)) {
      const mslp = Number(p.mslp);
      g.pointsByTau.set(tau, {
        tau,
        lat,
        lon,
        vmax: Number.isFinite(Number(p.maxwind)) ? Number(p.maxwind) : null,
        mslp: Number.isFinite(mslp) && mslp > 0 && mslp < 9990 ? mslp : null,
        ty: (p.stormtype || '').toUpperCase() || null
      });
    }
    if (tau === 0) {
      g.advdate = p.advdate || g.advdate;
      g.validtime0 = p.validtime;
    }
  }

  const storms = [];
  for (const g of groups.values()) {
    const points = [...g.pointsByTau.values()].sort((a, b) => a.tau - b.tau);
    if (!points.length) continue;
    const initDtg = initDtgFromAdvisory(g.advdate, g.validtime0);
    if (!initDtg) continue;
    storms.push({
      id: `${g.basin}${g.cy}`,
      basin: g.basin,
      cy: g.cy,
      name: g.name,
      initDtg,
      advisnum: g.advisnum,
      points,
      path: points.map(pt => [pt.lon, pt.lat, 0])
    });
  }
  storms.sort((a, b) => a.id.localeCompare(b.id));
  return storms;
}

async function loadFromMapServer(targetDtg) {
  const res = await fetch(FORECAST_POINTS_URL, {cache: 'no-cache'});
  if (!res.ok) throw new Error(`MapServer -> ${res.status}`);
  const fc = await res.json();
  let storms = stormsFromForecastGeoJson(fc);
  if (targetDtg && storms.length) {
    const near = storms.filter(s => pickClosestInitDtg([s.initDtg], targetDtg));
    if (near.length) storms = near;
  }
  console.info(
    '[ofcl] MapServer fallback (latest advisories only — mount /ofcl/storms for cycle-matched tracks)',
    storms.map(s => `${s.id}@${s.initDtg}`).join(', ') || '(none)'
  );
  return storms;
}

export async function loadOfclStormsForCycle(cycleKey) {
  const targetDtg = cycleKeyToDtg(cycleKey);
  if (!targetDtg) return [];

  try {
    const storms = await loadFromOfclApi(targetDtg);
    console.info(
      `[ofcl] cycle ${cycleKey}: ${storms.length} storm(s)`,
      storms.map(s => `${s.id}@${s.initDtg}`).join(', ') || '(none)'
    );
    return storms;
  } catch (err) {
    console.info('[ofcl] /ofcl/storms unavailable —', err.message || err);
  }

  try {
    return await loadFromMapServer(targetDtg);
  } catch (err) {
    console.warn('[ofcl] failed to load NHC forecast tracks', err);
    return [];
  }
}

export function positionsAtManifestTime(storms, manifestTs) {
  const validMs = manifestTimeToUtcMs(manifestTs);
  if (!Number.isFinite(validMs)) return [];
  const out = [];
  for (const storm of storms) {
    const pos = interpolateOfclPosition(storm.points, storm.initDtg, validMs);
    if (!pos) continue;
    out.push({
      id: storm.id,
      label: stormLabel(storm),
      name: storm.name,
      initDtg: storm.initDtg,
      lon: pos.lon,
      lat: pos.lat,
      vmax: pos.vmax,
      ty: pos.ty,
      hours: pos.hours
    });
  }
  return out;
}

export function createOfclLayers({PathLayer, ScatterplotLayer, TextLayer}, storms, positions) {
  if (!storms?.length) return [];

  // Guard: never let bad storm geometry take down the mesh layers.
  const safeStorms = storms.filter(
    s => Array.isArray(s.path) && s.path.length >= 2
      && s.path.every(p => Number.isFinite(p[0]) && Number.isFinite(p[1]))
  );
  const safePositions = (positions || []).filter(
    p => Number.isFinite(p.lon) && Number.isFinite(p.lat)
  );
  if (!safeStorms.length && !safePositions.length) return [];

  const layers = [];
  if (safeStorms.length) {
    layers.push(
      new PathLayer({
        id: 'ofcl-tracks',
        data: safeStorms,
        getPath: d => d.path,
        getColor: [220, 50, 50, 220],
        widthUnits: 'pixels',
        getWidth: 3,
        widthMinPixels: 2,
        jointRounded: true,
        capRounded: true,
        pickable: true,
        parameters: {depthCompare: 'always', depthWriteEnabled: false}
      })
    );
  }
  if (safePositions.length) {
    layers.push(
      new ScatterplotLayer({
        id: 'ofcl-positions',
        data: safePositions,
        getPosition: d => [d.lon, d.lat, 0],
        getRadius: d => 18000 + Math.min(d.vmax || 30, 150) * 250,
        radiusUnits: 'meters',
        radiusMinPixels: 10,
        radiusMaxPixels: 32,
        getFillColor: d => categoryColor(d.vmax),
        getLineColor: [255, 255, 255, 230],
        lineWidthUnits: 'pixels',
        getLineWidth: 2,
        stroked: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 120, 255],
        parameters: {depthCompare: 'always', depthWriteEnabled: false}
      }),
      new TextLayer({
        id: 'ofcl-labels',
        data: safePositions,
        getPosition: d => [d.lon, d.lat, 0],
        getText: d => d.label,
        getSize: 14,
        sizeUnits: 'pixels',
        getColor: [255, 255, 255, 245],
        getTextAnchor: 'start',
        getAlignmentBaseline: 'center',
        getPixelOffset: [14, 0],
        fontFamily: 'sans-serif',
        fontWeight: 'bold',
        pickable: false,
        parameters: {depthCompare: 'always', depthWriteEnabled: false}
      })
    );
  }
  return layers;
}
